import { useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import { MdAdd, MdClose, MdSave, MdShield } from 'react-icons/md'
import {
    getStoragePolicy,
    updateStoragePolicy,
} from '@/lib/api/master/master-settings-api'

/**
 * Política de armazenamento da plataforma.
 *
 * Fica no master, e não na configuração da empresa, porque é proteção nossa:
 * executável em repositório compartilhado de clínica é vetor de ransomware. Se
 * o cliente pudesse editar a lista, bastaria desmarcar para anular a proteção.
 */
export default function StoragePolicyPage() {
    const [extensions, setExtensions] = useState<string[]>([])
    const [newExtension, setNewExtension] = useState('')
    const [loading, setLoading] = useState(true)
    const [saving, setSaving] = useState(false)

    useEffect(() => {
        void (async () => {
            const response = await getStoragePolicy()
            if (response.success) {
                setExtensions(response.result?.blockedExtensions ?? [])
            }
            setLoading(false)
        })()
    }, [])

    const normalize = (value: string) => {
        const trimmed = value.trim().toLowerCase()
        if (!trimmed) return ''
        return trimmed.startsWith('.') ? trimmed : `.${trimmed}`
    }

    const handleAdd = () => {
        const extension = normalize(newExtension)
        if (!extension) return

        if (extensions.includes(extension)) {
            toast.info(`${extension} já está na lista`)
            setNewExtension('')
            return
        }

        setExtensions(previous => [...previous, extension].sort())
        setNewExtension('')
    }

    const handleSave = async () => {
        setSaving(true)
        const response = await updateStoragePolicy({
            blockedExtensions: extensions,
        })
        setSaving(false)

        if (!response.success) {
            toast.error(response.error || 'Não foi possível salvar a política')
            return
        }

        toast.success('Política de armazenamento salva com sucesso')
        setExtensions(response.result?.blockedExtensions ?? extensions)
    }

    if (loading) {
        return (
            <div className="flex justify-center py-20">
                <span className="loading loading-spinner loading-lg text-primary" />
            </div>
        )
    }

    return (
        <div className="p-4 sm:p-6 space-y-6">
            <div>
                <h1 className="text-2xl font-semibold flex items-center gap-2">
                    <MdShield className="text-primary" />
                    Política de Armazenamento
                </h1>
                <p className="text-sm text-base-content/60 mt-0.5">
                    Vale para todas as organizações da plataforma
                </p>
            </div>

            <div className="card bg-base-100 border border-base-300">
                <div className="card-body p-5 sm:p-6">
                    <h2 className="text-lg font-semibold mb-1">
                        Extensões bloqueadas
                    </h2>
                    <p className="text-sm text-base-content/60 mb-5">
                        Arquivos com estas extensões são recusados no envio, em
                        qualquer organização. Executável em repositório
                        compartilhado é vetor de ransomware
                    </p>

                    <div className="form-control w-full max-w-sm mb-5">
                        <label
                            className="label pb-1.5"
                            htmlFor="storage-new-extension"
                        >
                            <span className="label-text font-medium">
                                Adicionar extensão
                            </span>
                        </label>
                        <div className="join w-full">
                            <input
                                id="storage-new-extension"
                                type="text"
                                className="input input-bordered join-item w-full"
                                placeholder=".exe"
                                value={newExtension}
                                onChange={event =>
                                    setNewExtension(event.target.value)
                                }
                                onKeyDown={event => {
                                    if (event.key === 'Enter') {
                                        event.preventDefault()
                                        handleAdd()
                                    }
                                }}
                            />
                            <button
                                type="button"
                                className="btn btn-primary join-item"
                                onClick={handleAdd}
                            >
                                <MdAdd />
                                Adicionar
                            </button>
                        </div>
                        <span className="label-text-alt text-base-content/50 mt-1.5">
                            O ponto é opcional — <code>exe</code> vira{' '}
                            <code>.exe</code>
                        </span>
                    </div>

                    {extensions.length ? (
                        <div className="flex flex-wrap gap-2">
                            {extensions.map(extension => (
                                <span
                                    key={extension}
                                    className="badge badge-lg gap-1 font-mono"
                                >
                                    {extension}
                                    <button
                                        type="button"
                                        className="btn btn-ghost btn-xs btn-circle"
                                        onClick={() =>
                                            setExtensions(previous =>
                                                previous.filter(
                                                    e => e !== extension
                                                )
                                            )
                                        }
                                        title={`Remover ${extension}`}
                                    >
                                        <MdClose />
                                    </button>
                                </span>
                            ))}
                        </div>
                    ) : (
                        <div className="alert alert-warning py-2">
                            <span className="text-sm">
                                Nenhuma extensão bloqueada. Qualquer tipo de
                                arquivo poderá ser enviado, inclusive
                                executáveis.
                            </span>
                        </div>
                    )}
                </div>
            </div>

            <div className="card bg-base-100 border border-base-300">
                <div className="card-body p-5 sm:p-6">
                    <h2 className="text-lg font-semibold mb-1">
                        Tamanho máximo por arquivo
                    </h2>
                    <p className="text-sm text-base-content/60">
                        Definido <strong>por plano</strong>, em Planos de
                        Assinaturas &gt; Limites &gt; "Tamanho máx. por arquivo
                        (MB)".
                    </p>
                </div>
            </div>

            <div className="flex justify-end">
                <button
                    type="button"
                    className="btn btn-primary"
                    onClick={handleSave}
                    disabled={saving}
                >
                    {saving ? (
                        <span className="loading loading-spinner loading-sm" />
                    ) : (
                        <MdSave />
                    )}
                    Salvar política
                </button>
            </div>
        </div>
    )
}
